import React, { useState } from 'react';
import ImageUpload from '../components/ImageUpload';
import ProgressIndicator from '../components/ProgressIndicator';
import MoodboardDisplay from '../components/MoodboardDisplay';
import AuthModal from '../components/AuthModal';
import UserMenu from '../components/UserMenu';
import SaveMoodboard from '../components/SaveMoodboard';
import { uploadImage, getJobStatus, getMoodboardResult } from '../utils/api';
import { JobStatus, MoodboardState } from '../types';
import { useAuth } from '../contexts/AuthContext';
import PinterestLoginButton from '../components/PinterestLoginButton';


const Home: React.FC = () => {
  const [moodboardState, setMoodboardState] = useState<MoodboardState>({
    status: JobStatus.PENDING
  });
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { isAuthenticated, isLoading } = useAuth();

  const handleFileSelect = async (file: File, pinterestConsent: boolean) => {
    try {
      console.log('🚀 Starting upload for file:', file.name, 'Size:', file.size);
      console.log('📌 Pinterest consent:', pinterestConsent);
      setUploadedFile(file); // Store the uploaded file
      setMoodboardState({ status: JobStatus.PENDING });
      
      // Upload image
      console.log('📤 Calling uploadImage API...');
      const response = await uploadImage(file, pinterestConsent);
      console.log('✅ Upload successful:', response);
      
      setMoodboardState({
        jobId: response.job_id,
        status: response.status as JobStatus
      });

      // Start polling for status
      pollJobStatus(response.job_id);
    } catch (error: any) {
      console.error('❌ Upload failed - Full error:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      let errorMessage = 'Failed to upload image. Please try again.';
      if (error.response) {
        errorMessage = `Upload failed: ${error.response.status} - ${error.response.data?.detail || error.message}`;
      } else if (error.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      
      setMoodboardState({
        status: JobStatus.FAILED,
        error: errorMessage
      });
    }
  };

  const pollJobStatus = async (jobId: string) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max
    
    const poll = async () => {
      try {
        attempts++;
        const statusResponse = await getJobStatus(jobId);
        
        setMoodboardState(prev => ({
          ...prev,
          status: statusResponse.status as JobStatus,
          progress: statusResponse.progress
        }));

        if (statusResponse.status === JobStatus.COMPLETED) {
          // Fetch the final result
          const result = await getMoodboardResult(jobId);
          setMoodboardState(prev => ({
            ...prev,
            result
          }));
        } else if (statusResponse.status === JobStatus.FAILED) {
          setMoodboardState(prev => ({
            ...prev,
            error: statusResponse.error_message || 'Processing failed'
          }));
        } else if (statusResponse.status === JobStatus.PROCESSING && attempts < maxAttempts) {
          // Continue polling
          setTimeout(poll, 5000); // Poll every 5 seconds
        } else if (attempts >= maxAttempts) {
          setMoodboardState(prev => ({
            ...prev,
            status: JobStatus.FAILED,
            error: 'Processing timeout. Please try again.'
          }));
        }
      } catch (error) {
        console.error('Status polling failed:', error);
        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setMoodboardState(prev => ({
            ...prev,
            status: JobStatus.FAILED,
            error: 'Failed to check processing status'
          }));
        }
      }
    };

    // Start polling after a short delay
    setTimeout(poll, 2000);
  };

  const handleNewSession = () => {
    setMoodboardState({ status: JobStatus.PENDING });
    setUploadedFile(null); // Clear the uploaded file for new session
  };

  return (
    <div className="min-h-screen gradient-bg p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Authentication Header */}
        {!isLoading && (
          <div className="flex justify-end items-center mb-6">
            <div className="text-white">
              {isAuthenticated ? (
                <UserMenu />
              ) : (
                    <button
                      onClick={() => setIsAuthModalOpen(true)}
                      className="bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-gray-800 hover:bg-white/30 transition-colors font-medium"
                    >
                      Sign In / Sign Up
                    </button>
              )}
            </div>
          </div>
        )}
        {/* Header - Only show on initial state */}
        {moodboardState.status === JobStatus.PENDING && (
          <div className="text-center mb-8 md:mb-12 animate-fade-in px-4">
                 <div className="mb-6">
                   <span className="text-5xl md:text-6xl mb-4 block animate-bounce-slow">✨</span>
                   <h1 className="text-5xl sm:text-6xl md:text-8xl font-display font-bold text-white mb-6 drop-shadow-lg tracking-tight">
                     moorea
                   </h1>
                   <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-bold text-white/90 mb-6 drop-shadow-lg tracking-wide">
                     Elevating the everyday. Inspiring the individual. Defining the mood.
                   </h2>
                 </div>

            {/* Connect Pinterest for richer results */}
            <div className="mt-6 flex justify-center">
              <PinterestLoginButton />
            </div>
          </div>
        )}

        {/* Main Content Card */}
        <div className="card max-w-4xl mx-auto animate-slide-up">
          {/* Error Message */}
          {moodboardState.error && (
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6 rounded-lg animate-fade-in">
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-red-400 text-xl">⚠️</span>
                </div>
                <div className="ml-3">
                  <p className="text-red-700 font-medium">{moodboardState.error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Upload State */}
          {moodboardState.status === JobStatus.PENDING && (
            <div className="animate-fade-in">
              <ImageUpload 
                onFileSelect={handleFileSelect}
                isUploading={false}
              />
              
              {/* Most common aesthetics */}
              <div className="mt-8 text-center">
                <p className="text-gray-600 text-sm mb-3">
                  Most common aesthetics:
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-500">
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Cottagecore</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Dark Academia</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Minimalist</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Y2K</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Old Money</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full">Coastal</span>
                </div>
              </div>
            </div>
          )}

          {/* Processing State */}
          {moodboardState.status === JobStatus.PROCESSING && (
            <div className="animate-fade-in">
              <ProgressIndicator 
                status={moodboardState.status}
                progress={moodboardState.progress}
              />
            </div>
          )}

          {/* Results State */}
          {moodboardState.status === JobStatus.COMPLETED && moodboardState.result && (
            <div className="animate-fade-in">
              {/* Success header */}
              <div className="text-center mb-8">
                <span className="text-4xl mb-2 block">🎉</span>
                <h2 className="text-2xl font-display font-semibold text-gray-900 mb-2">
                  Your moodboard is ready!
                </h2>
                <p className="text-gray-600">
                  Scroll through your personalized style inspiration below
                </p>
              </div>

              <MoodboardDisplay result={moodboardState.result} originalImage={uploadedFile} />
              
              <div className="text-center mt-8 space-y-4">
                     {isAuthenticated && (
                       <SaveMoodboard 
                         result={moodboardState.result} 
                         originalImage={uploadedFile}
                         onSave={() => {}}
                       />
                     )}
                
                <button 
                  onClick={handleNewSession}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <span>✨</span>
                  Create Another Moodboard
                </button>
              </div>
            </div>
          )}

          {/* Failed State */}
          {moodboardState.status === JobStatus.FAILED && (
            <div className="text-center py-12 animate-fade-in">
              <span className="text-4xl mb-4 block">😞</span>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Oops! Something went wrong
              </h2>
              <p className="text-gray-600 mb-6">
                Don't worry, let's try again with a different photo
              </p>
              <button 
                onClick={handleNewSession}
                className="btn-primary inline-flex items-center gap-2"
              >
                <span>🔄</span>
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Footer - Only show on completed state */}
        {moodboardState.status === JobStatus.COMPLETED && (
          <div className="text-center mt-12 text-white/70 text-sm animate-fade-in">
            <p>Made with ✨ for fashion lovers • Share your moodboard with friends!</p>
          </div>
        )}
      </div>
      
      {/* Authentication Modal */}
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
    </div>
  );
};

export default Home;